import React from 'react';
import PropTypes from 'prop-types';
import ProductIconContainer from 'apex-web/lib/components/common/ProductIcon/ProductIconContainer';
import {
  formatNumberToLocale,
  formatPercentageValue
} from 'apex-web/lib/helpers/numberFormatter';
import { convertIncrementToIntDecimalPlaces } from 'apex-web/lib/helpers/decimalPlaces/decimalPlacesHelper';
import { getBEMClasses } from '../../helpers/cssClassesHelper';
import { buildSparklinePointString } from './sparkline';
import './DashboardSnapshotCards.css';

const classes = getBEMClasses('dashboard-snapshot-card');

const ACCENT_BY_SYMBOL = {
  BTC: '#f7931a',
  ETH: '#627eea',
  USDT: '#26a17b',
  USDC: '#2775ca'
};

function lineAccent(product1Symbol) {
  return ACCENT_BY_SYMBOL[product1Symbol] || 'var(--interior-accent, #38bdf8)';
}

const DashboardSnapshotCard = props => {
  const { instrument, product1, level1Data } = props;
  const {
    LastTradedPx = 0,
    Rolling24HrPxChangePercent = 0
  } = level1Data || {};
  const positive = Rolling24HrPxChangePercent >= 0;
  const accent = lineAccent(product1.ProductSymbol);
  const topEdge = buildSparklinePointString(
    instrument.InstrumentId,
    positive
  );
  const gradId = `dashboard-snapshot-spark-grad-${instrument.InstrumentId}`;
  const priceDecimals = convertIncrementToIntDecimalPlaces(
    instrument.PriceIncrement
  );
  const priceText = formatNumberToLocale(LastTradedPx, priceDecimals);
  const pctAbs = formatPercentageValue(Math.abs(Rolling24HrPxChangePercent));
  const pctLabel = positive ? `+ ${pctAbs}` : `- ${pctAbs}`;

  return (
    <article className={classes()}>
      <header className={classes('header')}>
        <ProductIconContainer
          iconFileName={product1.iconFileName}
          size={32}
        />
        <div className={classes('titles')}>
          <div className={classes('name')}>{product1.ProductFullName}</div>
          <div className={classes('symbol')}>{product1.ProductSymbol}</div>
        </div>
      </header>
      <div className={classes('chart')} style={{ color: accent }}>
        <svg
          className={classes('chart-svg')}
          viewBox="0 0 100 36"
          preserveAspectRatio="none"
          aria-hidden="true">
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polygon
            fill={`url(#${gradId})`}
            points={`0,36 ${topEdge} 100,36`}
          />
          <polyline
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            vectorEffect="non-scaling-stroke"
            points={topEdge}
          />
        </svg>
      </div>
      <footer className={classes('footer')}>
        <span className={classes('price')}>{priceText}</span>
        <span
          className={classes('change', {
            positive,
            negative: !positive
          })}>
          <span className={classes('pct')}>{pctLabel}</span>
          <span className={classes('badge')} aria-hidden="true">
            {positive ? '↑' : '↓'}
          </span>
        </span>
      </footer>
    </article>
  );
};

DashboardSnapshotCard.propTypes = {
  instrument: PropTypes.object.isRequired,
  product1: PropTypes.object.isRequired,
  level1Data: PropTypes.object.isRequired
};

export default DashboardSnapshotCard;
